import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface TableViewDB extends DBSchema {
    referenceData: {
        key: string;
        value: { type: string; data: any; lastUpdated: string };
    };
    orders: {
        key: string;
        value: any;
        indexes: { 'by-tableId': string; 'by-isOpen': string };
    };
    syncQueue: {
        key: number;
        value: {
            id?: number;
            type: string;
            url: string;
            method: string;
            body: string;
            createdAt: string;
            retryCount: number;
            status: 'pending' | 'processing' | 'failed';
        };
    };
}

let dbPromise: Promise<IDBPDatabase<TableViewDB>> | null = null;

function getDb() {
    if (!dbPromise) {
        dbPromise = openDB<TableViewDB>('tableview-offline', 1, {
            upgrade(db) {
                if (!db.objectStoreNames.contains('referenceData')) {
                    db.createObjectStore('referenceData', { keyPath: 'type' });
                }
                if (!db.objectStoreNames.contains('orders')) {
                    const orderStore = db.createObjectStore('orders', { keyPath: 'id' });
                    orderStore.createIndex('by-tableId', 'tableId');
                    orderStore.createIndex('by-isOpen', 'isOpen');
                }
                if (!db.objectStoreNames.contains('syncQueue')) {
                    db.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true });
                }
            },
        });
    }
    return dbPromise;
}

// Reference Data
export async function getRef(type: string): Promise<any | null> {
    const db = await getDb();
    const record = await db.get('referenceData', type);
    return record?.data ?? null;
}

export async function putRef(type: string, data: any): Promise<void> {
    const db = await getDb();
    await db.put('referenceData', { type, data, lastUpdated: new Date().toISOString() });
}

// Orders
export async function getAllOrders(): Promise<any[]> {
    const db = await getDb();
    return db.getAll('orders');
}

export async function getOrder(id: string): Promise<any | null> {
    const db = await getDb();
    return db.get('orders', id) ?? null;
}

export async function getActiveOrderByTableId(tableId: string): Promise<any | null> {
    const db = await getDb();
    const all = await db.getAllFromIndex('orders', 'by-tableId', tableId);
    return all.find(o => o.isOpen === true) ?? null;
}

export async function putOrder(order: any): Promise<void> {
    const db = await getDb();
    await db.put('orders', order);
}

export async function deleteOrder(id: string): Promise<void> {
    const db = await getDb();
    await db.delete('orders', id);
}

// Sync Queue
export async function enqueueMutation(mutation: {
    type: string;
    url: string;
    method: string;
    body: string;
}): Promise<void> {
    const db = await getDb();
    await db.add('syncQueue', {
        ...mutation,
        createdAt: new Date().toISOString(),
        retryCount: 0,
        status: 'pending',
    });
}

export async function getPendingMutations(): Promise<any[]> {
    const db = await getDb();
    const all = await db.getAll('syncQueue');
    return all.filter(m => m.status === 'pending');
}

export async function removeMutation(id: number): Promise<void> {
    const db = await getDb();
    await db.delete('syncQueue', id);
}

export async function updateMutationRetry(id: number, retryCount: number): Promise<void> {
    const db = await getDb();
    const mutation = await db.get('syncQueue', id);
    if (mutation) {
        mutation.retryCount = retryCount;
        await db.put('syncQueue', mutation);
    }
}
